/**
 * New Relic Browser Adapter
 * Integrates with New Relic Browser for RUM using the official npm package
 * @see https://github.com/newrelic/newrelic-browser-agent
 */

import { BaseAnalyticsAdapter } from './adapter.interface'
import type {
  AdapterConfig,
  PageViewEvent,
  TrackEvent,
  IdentifyEvent,
  ErrorEvent,
  PerformanceEvent,
  NewRelicAdapterOptions,
} from '../../domain/types'

type NewRelicBrowserAgent = {
  setCustomAttribute: (key: string, value: unknown) => void
  addPageAction: (name: string, attributes?: Record<string, unknown>) => void
  setUserId: (id: string) => void
  noticeError: (error: Error, attributes?: Record<string, unknown>) => void
}

type BrowserAgentConstructor = new (options: unknown) => NewRelicBrowserAgent

/**
 * New Relic Browser adapter using the official npm package
 * Sends analytics to New Relic Browser RUM
 */
export class NewRelicAdapter extends BaseAnalyticsAdapter {
  readonly name = 'newrelic'

  private agent: NewRelicBrowserAgent | null = null

  async initialize(config: AdapterConfig): Promise<void> {
    await super.initialize(config)

    const options = config.options as NewRelicAdapterOptions

    // Validate required options
    if (!options?.licenseKey || !options?.applicationId || !options?.accountId) {
      console.warn('[Analytics] New Relic: Missing required configuration (licenseKey, applicationId, accountId)')
      return
    }

    try {
      if (typeof window === 'undefined') return

      let BrowserAgent: BrowserAgentConstructor | undefined
      try {
        const moduleId = '@newrelic/browser-agent/loaders/browser-agent'
        const mod = await import(/* @vite-ignore */ moduleId)
        const maybeCtor = (mod as any)?.BrowserAgent ?? (mod as any)?.default?.BrowserAgent ?? (mod as any)?.default
        if (typeof maybeCtor === 'function') BrowserAgent = maybeCtor as BrowserAgentConstructor
      } catch {
        console.warn('[Analytics] New Relic: Package not installed, skipping initialization')
        return
      }

      if (!BrowserAgent) {
        console.warn('[Analytics] New Relic: BrowserAgent export not found, skipping initialization')
        return
      }

      // Initialize the New Relic Browser Agent
      this.agent = new BrowserAgent({
        init: {
          distributed_tracing: { enabled: true },
          privacy: { cookies_enabled: true },
          ajax: { deny_list: ['bam.eu01.nr-data.net'] },
          // Enable SPA monitoring
          spa: { enabled: true },
          // Enable session trace
          session_trace: { enabled: true },
          // Enable page view timing
          page_view_timing: { enabled: true },
          // Enable JS errors
          jserrors: { enabled: true },
        },
        info: {
          beacon: 'bam.eu01.nr-data.net',
          errorBeacon: 'bam.eu01.nr-data.net',
          licenseKey: options.licenseKey,
          applicationID: options.applicationId,
          sa: 1,
        },
        loader_config: {
          accountID: options.accountId,
          trustKey: options.trustKey || options.accountId,
          agentID: options.applicationId,
          licenseKey: options.licenseKey,
          applicationID: options.applicationId,
        },
      })

      // Set environment tag for filtering
      if (options.environment) {
        this.agent.setCustomAttribute('environment', options.environment)
      }

      // Set app name tag
      if (options.appName) {
        this.agent.setCustomAttribute('appName', options.appName)
      }

      console.log('[Analytics] New Relic adapter initialized successfully')
    } catch (error) {
      console.error('[Analytics] Failed to initialize New Relic:', error)
    }
  }

  trackPageView(event: PageViewEvent): void {
    if (!this.shouldTrack() || !this.agent) return

    // Set custom attributes for better context
    this.agent.setCustomAttribute('pagePath', event.pagePath)
    this.agent.setCustomAttribute('pageTitle', event.title)
    this.agent.setCustomAttribute('routeName', event.routeName || 'unknown')
    this.agent.setCustomAttribute('deviceType', event.deviceType)
    this.agent.setCustomAttribute('locale', event.locale)
    this.agent.setCustomAttribute('isRtl', String(event.isRtl))

    // Add page action for explicit page view event
    this.agent.addPageAction('pageView', {
      pagePath: event.pagePath,
      title: event.title,
      routeName: event.routeName,
      referrer: event.referrer,
      previousPageDuration: event.previousPageDuration,
      deviceType: event.deviceType,
      locale: event.locale,
    })
  }

  track(event: TrackEvent): void {
    if (!this.shouldTrack() || !this.agent) return

    this.agent.addPageAction(event.eventName, {
      category: event.category,
      action: event.action,
      label: event.label,
      value: event.value,
      ...event.properties,
      timestamp: event.timestamp,
    })
  }

  identify(event: IdentifyEvent): void {
    if (!this.shouldTrack() || !this.agent) return

    // Set user ID for New Relic
    this.agent.setUserId(event.userId)

    // Set user traits as custom attributes
    if (event.traits.accountType) {
      this.agent.setCustomAttribute('accountType', event.traits.accountType)
    }
    if (event.traits.company) {
      this.agent.setCustomAttribute('company', event.traits.company)
    }
    if (event.traits.customerId) {
      this.agent.setCustomAttribute('customerId', event.traits.customerId)
    }
    if (event.traits.tier) {
      this.agent.setCustomAttribute('tier', event.traits.tier)
    }
    if (event.traits.roles) {
      this.agent.setCustomAttribute('roles', event.traits.roles.join(','))
    }
    if (event.traits.preferredLanguage) {
      this.agent.setCustomAttribute('preferredLanguage', event.traits.preferredLanguage)
    }
  }

  trackError(event: ErrorEvent): void {
    if (!this.agent) return // Always track errors if agent is available

    const error = new Error(event.errorMessage)
    error.name = event.errorName
    if (event.stackTrace) {
      error.stack = event.stackTrace
    }

    this.agent.noticeError(error, {
      severity: event.severity,
      componentName: event.componentName,
      handled: event.handled,
      fingerprint: event.fingerprint,
      ...event.context,
    })
  }

  trackPerformance(event: PerformanceEvent): void {
    if (!this.shouldTrack() || !this.agent) return

    // Record Web Vitals as page actions
    this.agent.addPageAction(`webVitals.${event.metricName}`, {
      value: event.value,
      rating: event.rating,
      navigationType: event.navigationType,
      ...event.attribution,
    })

    // Also set as custom attribute for dashboard filtering
    this.agent.setCustomAttribute(`webVital.${event.metricName}`, event.value)
    this.agent.setCustomAttribute(`webVital.${event.metricName}.rating`, event.rating)
  }

  reset(): void {
    // New Relic doesn't have a built-in reset
    // User ID will persist until page refresh
    console.log('[Analytics] New Relic identity reset (page refresh required for full reset)')
  }

  isReady(): boolean {
    return this.isInitialized && this.agent !== null
  }
}
